import { useEffect, useRef, useState, type ReactNode } from 'react';
import { validateExpectedMin, validateSolution } from '@lw/shared/mission-fsm';
import { WARN_WINDOW_MS } from '@lw/shared/constants';
import type { ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import { actedBy, fmtTime } from '../app/helpers.js';
import { ActedBy, EstimateChip, PeerChip, ReadyAge, SolutionSummary, SyncChip } from '../components/Chips.js';
import { CountdownPanel } from '../components/Countdown.js';
import { CancelDialog, ReviewCard } from '../components/Common.js';
import { MissionHistory, useSeat, type ViewOut } from './UavView.js';

export function FdcView({ snapshot: s }: { snapshot: ClientSnapshot }): ViewOut {
  const { rt, state } = useRuntime();
  const ctrl = useSeat();
  const events = state.events;
  const [expected, setExpected] = useState('6');
  const draft = s.solutionDraft;
  const [gtl, setGtl] = useState(draft?.gtl ? String(draft.gtl.value) : '');
  const [gtlUnit, setGtlUnit] = useState<'mils' | 'deg'>(draft?.gtl?.unit === 'deg' ? 'deg' : 'mils');
  const [code, setCode] = useState(draft?.laserCode ?? '');
  const [F, setF] = useState(draft ? String(draft.shellFlightTimeS) : '');
  const [L, setL] = useState(draft ? String(draft.laserOnLeadS) : '');
  const [cancelDlg, setCancelDlg] = useState(false);
  const layoutRef = useRef(0);
  useEffect(() => {
    layoutRef.current = performance.now();
  }, [s.state]);

  const next = (type: 'SOLUTION_NEXT' | 'PROGRAMMING_NEXT' | 'LOADING_NEXT', payload: Record<string, unknown> = {}): void => void rt.client.send(type, payload, { basedOnSeq: s.seq });
  const back = (): void => void rt.client.send('BACK', {}, { basedOnSeq: s.seq });
  const backBtn = <button className="btn ghost narrow" disabled={!ctrl} onClick={back} data-testid="back">Back</button>;

  switch (s.state) {
    case 'IDLE':
    case 'SAVED':
    case 'DISCARDED':
      return {
        main: (
          <>
            <div className="card"><div className="eyebrow">Awaiting call for fire</div><h2 className="big">No active mission</h2><p>The UAV's call for fire will appear here with its target.</p></div>
            <MissionHistory />
          </>
        ),
      };
    case 'CFF': {
      const v = validateExpectedMin(Number(expected));
      const age = actedBy(events, 'CFF_CREATED');
      return {
        main: (
          <div className="card amber">
            <div className="eyebrow">Incoming call for fire</div>
            <h2 className="big">{s.target?.value}</h2>
            <p>From {age ?? 'UAV'} · <CffAge snapshot={s} /> ago</p>
            <div className="field" style={{ marginTop: 14 }}>
              <label htmlFor="expected">Expected time to process (minutes)</label>
              <input id="expected" className="mono" inputMode="numeric" pattern="[0-9]*" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="expected" />
              <div className="hint">Shown to the UAV and Observer as an elapsed-time chip.</div>
            </div>
          </div>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn confirm" disabled={!v.ok || !ctrl} data-testid="confirm-mission" onClick={() => rt.client.send('START_MISSION', { expectedMin: Number(expected) }, { basedOnSeq: s.seq })}>Confirm fire mission</button>
          </div></div>
        ),
      };
    }
    case 'SOLUTION': {
      const payload = { gtl: gtl === '' ? null : { unit: gtlUnit, value: Number(gtl) }, laserCode: code, shellFlightTimeS: Number(F), laserOnLeadS: Number(L) };
      const v = validateSolution(payload);
      const err = (k: string): string | undefined => v.errors[k];
      return {
        main: (
          <div className="card">
            <div className="eyebrow">Solution · {s.target?.value}{draft ? ' · retained' : ''}</div>
            <div className="field" style={{ marginTop: 10 }}>
              <label htmlFor="gtl">Gun-target line <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <div className="row">
                <input id="gtl" className="mono" inputMode="decimal" value={gtl} placeholder={gtlUnit === 'mils' ? '0–6399.9' : '0–359.9'} style={{ flex: 1 }} onChange={(e) => setGtl(e.target.value)} />
                <div className="seg"><button className={gtlUnit === 'mils' ? 'on' : ''} onClick={() => setGtlUnit('mils')}>mils</button><button className={gtlUnit === 'deg' ? 'on' : ''} onClick={() => setGtlUnit('deg')}>deg</button></div>
              </div>
              <div className={`hint ${err('gtl') ? 'err' : ''}`}>{err('gtl') ?? 'May be left empty'}</div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="code">Laser code</label>
              <input id="code" className="mono" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={code} placeholder="XXXX" onChange={(e) => setCode(e.target.value)} data-testid="laser-code" />
              <div className={`hint ${err('laserCode') ? '' : 'ok'}`}>{err('laserCode') ?? 'Read back the code below to the UAV'}</div>
            </div>
            <div className="codeecho">{/^\d{4}$/.test(code) ? code : '····'}<small>Laser code read-back</small></div>
            <div className="grid2">
              <div className="field"><label htmlFor="F">Shell flight time (s)</label><input id="F" className="mono" inputMode="decimal" value={F} onChange={(e) => setF(e.target.value)} data-testid="flight" /><div className={`hint ${err('shellFlightTimeS') ? 'err' : ''}`}>{err('shellFlightTimeS') ?? '8.0–300.0 s'}</div></div>
              <div className="field"><label htmlFor="L">Laser on before impact (s)</label><input id="L" className="mono" inputMode="decimal" value={L} onChange={(e) => setL(e.target.value)} data-testid="lead" /><div className={`hint ${err('laserOnLeadS') ? 'err' : ''}`}>{err('laserOnLeadS') ?? '1.0–60.0 s, at least 1 s less than the flight time'}</div></div>
            </div>
            {v.warnings.map((w) => <div key={w} className="hint" style={{ color: 'var(--amber)' }}>{w}</div>)}
            <div className="row" style={{ marginTop: 12 }}><EstimateChip /></div>
          </div>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            {backBtn}
            <button className="btn primary" disabled={!v.ok || !ctrl} data-testid="next" onClick={() => next('SOLUTION_NEXT', payload)}>Next</button>
          </div></div>
        ),
      };
    }
    case 'PROGRAMMING':
      return {
        main: <div className="card amber"><div className="eyebrow">Program the shell</div><h2 className="big">Set the code on the shell</h2><p>Programming happens on the programmer, not here. Press NEXT when the shell reports the code.</p><div className="codeecho">{s.solution?.laserCode}<small>Laser code</small></div><div className="row"><EstimateChip /></div><ActedBy by={actedBy(events, 'SOLUTION_ENTERED')} /></div>,
        actions: <div className="actionbar"><div className="inner">{backBtn}<button className="btn primary" disabled={!ctrl} data-testid="next" onClick={() => next('PROGRAMMING_NEXT')}>Next</button></div></div>,
      };
    case 'LOADING':
      return {
        main: <div className="card amber"><div className="eyebrow">Load the weapon</div><h2 className="big">Load the gun</h2><p>Press NEXT when the round is loaded and the gun is laid.</p><SolutionSummary /><div className="row" style={{ marginTop: 10 }}><EstimateChip /></div><ActedBy by={actedBy(events, 'PROGRAMMING_DONE')} /></div>,
        actions: <div className="actionbar"><div className="inner">{backBtn}<button className="btn primary" disabled={!ctrl} data-testid="next" onClick={() => next('LOADING_NEXT')}>Next</button></div></div>,
      };
    case 'WEAPON_READY':
      return {
        main: (
          <>
            <div className="card"><div className="eyebrow">Weapon ready</div><h2 className="big">Awaiting UAV ready</h2><p>The UAV crew is doing its audio check and confirming the aircraft is set.</p><div className="row" style={{ marginTop: 10 }}><PeerChip /><SyncChip /></div><SolutionSummary /><ActedBy by={actedBy(events, 'LOADING_DONE')} /></div>
            {s.lastCancel?.band === 'CHECK_FIRE' && !s.uav.ceaseLasingAckAt ? <div className="card red"><div className="eyebrow">Cease lasing</div><p>The fire was cancelled inside the laser window. Awaiting the UAV's CEASE LASING acknowledgement.</p></div> : null}
            {s.lastCancel?.band === 'CHECK_FIRE' && s.uav.ceaseLasingAckAt ? <div className="card green"><div className="eyebrow">Cease lasing</div><p>Acknowledged by the UAV.</p></div> : null}
            <div className="card"><div className="eyebrow">Fire control</div><p>SHOT unlocks when the UAV reports ready. The button never moves, so your thumb always knows where it is.</p></div>
          </>
        ),
        actions: (
          <div className="actionbar stack"><div className="inner" style={{ flexDirection: 'column', gap: 8 }}>
            <button className="btn fire ghosted" disabled style={{ margin: 0, width: '100%' }}>Shot</button>
            <button className="btn ghost" disabled={!ctrl} onClick={back} data-testid="back">Back</button>
          </div></div>
        ),
      };
    case 'UAV_READY': {
      return {
        main: (
          <div className="card green">
            <div className="eyebrow">UAV ready</div>
            <h2 className="big">Cleared to fire</h2>
            <p>Press SHOT at the instant the gun fires — not before. The press time is what every countdown is built from.</p>
            <dl className="kv">
              <dt>UAV link</dt><dd><PeerChip /></dd>
              <dt>Sync</dt><dd><SyncChip /></dd>
              <dt>UAV ready for</dt><dd><ReadyAge />{s.uav.readyStale ? <span className="amber"> (stale)</span> : null}</dd>
              <dt>Laser code</dt><dd>{s.solution?.laserCode}</dd>
              <dt>Flight / laser</dt><dd>{s.solution?.shellFlightTimeS} s / {s.solution?.laserOnLeadS} s before</dd>
            </dl>
            <ActedBy by={actedBy(events, 'UAV_READY_SET')} />
          </div>
        ),
        actions: (
          <div className="actionbar stack"><div className="inner" style={{ flexDirection: 'column', gap: 8 }}>
            <FireButton snapshot={s} ctrl={ctrl} layoutAt={layoutRef} />
            <button className="btn ghost" disabled={!ctrl} onClick={back} data-testid="back">Back — clears UAV readiness</button>
          </div></div>
        ),
      };
    }
    case 'FIRE_REVIEW':
      return {
        main: <ReviewCard snapshot={s} forFdc />,
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn outline-danger narrow" disabled={!ctrl} data-testid="cancel-review" onClick={() => rt.client.send('CANCEL_FIRE', {}, { expectedState: 'FIRE_REVIEW' })}>Cancel</button>
            <button className="btn primary" disabled={!ctrl} data-testid="confirm-late" onClick={() => rt.client.send('CONFIRM_LATE_FIRE', {}, { expectedState: 'FIRE_REVIEW' })}>Confirm late fire</button>
          </div></div>
        ),
      };
    case 'IN_FLIGHT': {
      const tl = s.timeline;
      const now = rt.client.chrono.serverNow();
      const check = !!tl && now !== null && now >= tl.tLaserOn - WARN_WINDOW_MS;
      return {
        main: (
          <>
            <CountdownPanel snapshot={s} />
            {cancelDlg ? <CancelDialog check={check} onClose={() => setCancelDlg(false)} onConfirm={() => { setCancelDlg(false); rt.client.send('CANCEL_FIRE', {}, { expectedState: 'IN_FLIGHT' }); }} /> : null}
          </>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn outline-amber huge" disabled={!ctrl} data-testid="cancel-fire" onClick={() => setCancelDlg(true)}>{check ? 'Check fire' : 'Cancel fire'}</button>
          </div></div>
        ),
      };
    }
    case 'AWAITING_DISPOSITION':
      return {
        main: (
          <div className="card">
            <div className="eyebrow">Mission complete</div>
            <h2 className="big">{s.target?.value} · impact {fmtTime(s.timeline?.tImpact, true)}</h2>
            <p>The UAV decides whether to save or discard the log. {s.uav.laserOffAt ? 'Laser off confirmed.' : s.uav.laserOffTimeout ? 'Laser off unconfirmed.' : 'Awaiting LASER OFF from the UAV.'}</p>
            <SolutionSummary />
            <div className="row" style={{ marginTop: 10 }}><a className="btn" href={`/api/v1/missions/${s.missionId}/export?device=${rt.id.deviceId}&user=${rt.id.user}&role=${rt.id.role}`} download>Export log</a></div>
          </div>
        ),
      };
    default:
      return { main: null };
  }
}

function CffAge({ snapshot }: { snapshot: ClientSnapshot }): ReactNode {
  const { rt } = useRuntime();
  const created = rt.client.events.find((e) => e.type === 'CFF_CREATED')?.tEvent ?? null;
  const now = rt.client.chrono.serverNow();
  void snapshot;
  return <span className="mono">{created === null || now === null ? '—' : `${Math.max(0, Math.floor((now - created) / 1000))} s`}</span>;
}

/**
 * §11.3 FireButton: pointerdown stamps and persists the press before any I/O; taps within 500 ms of
 * a layout change are ignored; the link acknowledgement is attached as flags, never a gate.
 */
function FireButton({ snapshot, ctrl, layoutAt }: { snapshot: ClientSnapshot; ctrl: boolean; layoutAt: { current: number } }): ReactNode {
  const { rt } = useRuntime();
  const press = (): void => {
    if (performance.now() - layoutAt.current < 500) return;
    const ownRed = rt.client.indicator.current().colour === 'RED';
    const uavLost = snapshot.links['UAV']?.conn === 'LOST' || (rt.client.pair !== null && !rt.client.pair.known);
    const linkFlags: string[] = [];
    if (ownRed) linkFlags.push('FIRE_WITH_OWN_LINK_LOST');
    else if (uavLost) linkFlags.push('FIRE_WITH_UAV_LINK_LOST');
    rt.pressFire(linkFlags);
  };
  return (
    <button className="btn fire" disabled={!ctrl} style={{ margin: 0, width: '100%' }} data-testid="shot" onPointerDown={press} onKeyDown={(e) => e.preventDefault()}>
      Shot
    </button>
  );
}
